import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, Calendar } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { HERO_VIDEO_PATH, HERO_POSTER_URL } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VIDEO_SRC = `${BACKEND_URL}${HERO_VIDEO_PATH}`;

const detectEnv = () => {
    if (typeof window === "undefined") return { reducedMotion: false, isMobile: false, isiOS: false };
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const ua = window.navigator.userAgent || "";
    const isiOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = window.innerWidth < 768 || isiOS;
    return { reducedMotion, isMobile, isiOS };
};

/**
 * Scroll-scrubbed video hero with performance optimizations:
 * - Throttled currentTime updates (max ~12fps) to avoid decoder thrashing
 * - fastSeek() when available
 * - Progress tracked via ref for 60fps UI, state only for React every 2nd frame
 * - Stepped cue-point progression as primary behavior
 * - 200vh scroll height for snappier feel
 */
const ScrollVideoHero = () => {
    const { t } = useLanguage();
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const progressRef = useRef(0);
    const lastSeekRef = useRef(0);
    const lastStateRef = useRef(0);

    const [progress, setProgress] = useState(0);
    const [env] = useState(detectEnv);
    const [videoReady, setVideoReady] = useState(false);
    const [videoFailed, setVideoFailed] = useState(false);

    const useScrub = !env.reducedMotion && !env.isMobile;

    // -------------------------------------------------------------------------
    // Scroll-driven video scrub — performance-optimized
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!useScrub) return;
        const video = videoRef.current;
        const container = containerRef.current;
        if (!video || !container) return;

        let rafId = 0;
        let target = 0;
        let current = 0;
        let frameCount = 0;

        const SEEK_INTERVAL_MS = 80;      // max ~12.5 seeks/sec
        const TIME_DELTA_THRESHOLD = 0.12; // only seek if > 0.12s diff
        const SCROLL_HEIGHT_VH = 200;     // reduced from 300vh

        const computeProgress = () => {
            const rect = container.getBoundingClientRect();
            const total = container.offsetHeight - window.innerHeight;
            if (total <= 0) return 0;
            const scrolled = Math.min(Math.max(-rect.top, 0), total);
            return scrolled / total;
        };

        const onScroll = () => {
            target = computeProgress();
        };

        const tick = () => {
            // Smooth lerp toward target
            current += (target - current) * 0.22;
            if (Math.abs(target - current) < 0.001) current = target;

            progressRef.current = current;
            frameCount++;

            // Update React state every 3rd frame (~20fps) for UI
            if (frameCount % 3 === 0) {
                setProgress(current);
            }

            // Throttled video seek
            const now = performance.now();
            const timeSinceLastSeek = now - lastSeekRef.current;

            if (
                timeSinceLastSeek >= SEEK_INTERVAL_MS &&
                video.readyState >= 2 &&
                video.duration &&
                isFinite(video.duration)
            ) {
                const t = current * video.duration;
                if (Math.abs(video.currentTime - t) > TIME_DELTA_THRESHOLD) {
                    lastSeekRef.current = now;
                    try {
                        if (typeof video.fastSeek === "function") {
                            video.fastSeek(t);
                        } else {
                            video.currentTime = t;
                        }
                    } catch (_) {
                        /* no-op */
                    }
                }
            }

            rafId = requestAnimationFrame(tick);
        };

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [useScrub, videoReady]);

    // Non-scrub fallback (mobile / reduced-motion)
    useEffect(() => {
        if (useScrub) return;
        const container = containerRef.current;
        if (!container) return;
        const onScroll = () => {
            const rect = container.getBoundingClientRect();
            const total = container.offsetHeight - window.innerHeight;
            if (total <= 0) {
                setProgress(0);
                return;
            }
            const scrolled = Math.min(Math.max(-rect.top, 0), total);
            setProgress(scrolled / total);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [useScrub]);

    const handleLoaded = () => {
        const v = videoRef.current;
        if (v) {
            v.pause();
            try {
                v.currentTime = 0.001;
            } catch (_) {
                /* no-op */
            }
            setVideoReady(true);
        }
    };

    const handleError = () => {
        setVideoFailed(true);
    };

    // -------------------------------------------------------------------------
    // Text states — stepped cue-point progression for smoother feel
    // -------------------------------------------------------------------------
    const states = t.hero.states;

    const stateOpacity = (i) => {
        const centers = [0.18, 0.5, 0.82];
        const width = 0.24;
        const d = Math.abs(progress - centers[i]);
        const v = 1 - Math.max(0, (d - 0.08) / (width - 0.08));
        return Math.max(0, Math.min(1, v));
    };

    return (
        <section
            id="top"
            ref={containerRef}
            data-testid="hero-section"
            className="relative w-full"
            style={{ height: useScrub ? "200vh" : "100vh" }}
        >
            <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
                {env.reducedMotion || videoFailed ? (
                    <img
                        src={HERO_POSTER_URL}
                        alt="BrowsEMS hero"
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="hero-poster-fallback"
                    />
                ) : (
                    <video
                        ref={videoRef}
                        data-testid="hero-video"
                        src={VIDEO_SRC}
                        poster={HERO_POSTER_URL}
                        muted
                        playsInline
                        preload="metadata"
                        loop={!useScrub}
                        autoPlay={!useScrub}
                        onLoadedMetadata={handleLoaded}
                        onCanPlay={handleLoaded}
                        onError={handleError}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ willChange: "transform" }}
                    />
                )}

                {/* Softer gradient overlays — video must remain dominant */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/45 pointer-events-none" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_45%,rgba(0,0,0,0.3)_100%)] pointer-events-none" />

                {/* Top kicker — minimal so video stays primary */}
                <div className="absolute top-6 left-0 right-0 pointer-events-none">
                    <div className="container-x">
                        <span
                            data-testid="hero-kicker"
                            className="font-body text-[11px] tracking-[0.35em] uppercase text-white/75"
                        >
                            {t.hero.kicker}
                        </span>
                    </div>
                </div>

                {/* Center text — restrained copy, video-forward */}
                <div className="absolute inset-0 flex items-center pointer-events-none">
                    <div className="container-x w-full">
                        <div className="max-w-2xl relative min-h-[240px] sm:min-h-[280px]">
                            {states.map((s, i) => (
                                <div
                                    key={i}
                                    data-testid={`hero-text-state-${i}`}
                                    className="absolute inset-0 flex flex-col justify-center"
                                    style={{
                                        opacity: stateOpacity(i),
                                        transition: "opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                                    }}
                                >
                                    <span className="font-heading text-2xl sm:text-3xl text-white/80 leading-none mb-3">
                                        {s.eyebrow}
                                    </span>
                                    <h1 className="font-body text-white text-4xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] mb-4 drop-shadow-[0_2px_24px_rgba(0,0,0,0.3)]">
                                        {s.title}
                                    </h1>
                                    <p className="font-body text-white/75 text-base sm:text-lg max-w-xl leading-relaxed">
                                        {s.caption}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* CTAs + scroll hint */}
                        <div className="absolute left-0 right-0 bottom-10 container-x flex flex-wrap items-end justify-between gap-6 pointer-events-auto">
                            <div className="flex flex-wrap items-center gap-3">
                                <a
                                    href="#booking"
                                    data-testid="hero-cta"
                                    className="inline-flex items-center gap-3 px-6 py-3 rounded-[0.3rem] bg-white text-foreground font-body font-medium text-sm transition-all duration-300 hover:bg-primary hover:text-white active:scale-[0.98]"
                                >
                                    <Calendar size={16} />
                                    {t.hero.cta}
                                </a>
                                <a
                                    href="#services"
                                    data-testid="hero-secondary-cta"
                                    className="inline-flex items-center gap-3 px-6 py-3 rounded-[0.3rem] border border-white/50 text-white font-body font-medium text-sm transition-all duration-300 hover:bg-white hover:text-foreground active:scale-[0.98]"
                                >
                                    {t.hero.secondaryCta}
                                    <ArrowDown size={16} />
                                </a>
                            </div>

                            {useScrub && (
                                <div className="hidden sm:flex items-center gap-3 text-white/50 text-[10px] font-body uppercase tracking-[0.3em]">
                                    <span>{t.hero.scrollHint}</span>
                                    <div className="relative w-24 h-px bg-white/20 overflow-hidden">
                                        <div
                                            className="absolute inset-y-0 left-0 bg-white/70"
                                            style={{ width: `${Math.round(progress * 100)}%` }}
                                        />
                                    </div>
                                    <span data-testid="hero-progress" className="tabular-nums">
                                        {String(Math.round(progress * 100)).padStart(2, "0")}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom-right cursive accent */}
                <div className="absolute bottom-6 right-6 hidden md:block pointer-events-none select-none">
                    <span className="font-heading text-white/20 text-2xl">crafted by hand</span>
                </div>
            </div>
        </section>
    );
};

export default ScrollVideoHero;
